// Copyright (c) 2026 GLOBAL MICRO IT TECHNOLOGY SOLUTIONS LIMITED
// Company No. 758762, Dublin, Ireland
// All rights reserved. Proprietary and confidential.

import { useState, useCallback } from 'react';

export type SortDirection = 'asc' | 'desc';

export interface SortState<TField extends string> {
  /** Currently sorted column key */
  sortColumn: TField;
  /** Current sort direction */
  sortDirection: SortDirection;
  /** Click handler for column headers. Toggles direction if same column, resets to 'asc' otherwise. */
  handleSort: (column: TField) => void;
}

export interface UseSortableTableOptions<TField extends string> {
  /** Initial sort column */
  defaultColumn: TField;
  /** Initial sort direction (default: 'asc') */
  defaultDirection?: SortDirection;
  /** Optional callback fired on any sort change (e.g. reset pagination to page 0) */
  onSortChange?: () => void;
}

/**
 * Shared hook for sortable table state management.
 *
 * Manages column + direction state and provides a click handler.
 * Each page keeps its own comparator logic in useMemo — the hook only owns state.
 *
 * @example
 * type Col = 'control' | 'status' | 'priority';
 * const { sortColumn, sortDirection, handleSort } = useSortableTable<Col>({
 *   defaultColumn: 'control',
 *   onSortChange: () => setPage(0),
 * });
 */
export function useSortableTable<TField extends string>(
  options: UseSortableTableOptions<TField>,
): SortState<TField> {
  const { defaultColumn, defaultDirection = 'asc', onSortChange } = options;

  // #2036: one state object updated by a PURE updater. The previous form toggled
  // direction via a setSortDirection call *inside* the setSortColumn updater — a side
  // effect React double-invokes under <StrictMode>, enqueuing asc→desc→asc so clicking
  // the already-active header never flipped direction on the dev server (prod, single
  // invoke, was correct — which disguised it as a server-side sorting bug).
  const [sort, setSort] = useState<{ column: TField; direction: SortDirection }>({
    column: defaultColumn,
    direction: defaultDirection,
  });

  const handleSort = useCallback(
    (column: TField) => {
      setSort((prev) => ({
        column,
        direction: prev.column === column ? (prev.direction === 'asc' ? 'desc' : 'asc') : 'asc',
      }));
      onSortChange?.();
    },
    [onSortChange],
  );

  return { sortColumn: sort.column, sortDirection: sort.direction, handleSort };
}
